import React, { createContext, SetStateAction, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { SortingState } from '@tanstack/react-table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePost } from '../API';
import { ReportFilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import { logAdminPanelError } from '../utils/adminPanel.utils';
import { ReportConfig } from '@aidkitorg/aidkit/lib/admin_panel/reports/domain/ReportConfig';
import { CustomQueryTable } from '@aidkitorg/types/lib/survey';
import InterfaceContext from '../Context';

export type Report = {
  id: string;
  title: string;
  description: string;
  created_by: string;
  created_by_name: string;
  created_at: number;
  updated_at: number;
  view_only: boolean;
  status: 'draft' | 'published';
  config?: ReportConfig;
  /** Reports fetched from the database (legacy system) cannot be edited or cloned */
  _fromDb?: boolean;
  /** Reports backed by distro Custom Query should be view-only in Admin Panel FE */
  _isCustomQuery?: boolean;
  locked?: boolean;
};

export type ReportWithConfig = Report & {
  config: ReportConfig;
};

export type ReportDataRow = Record<string, any>;

export type ReportData = {
  data: ReportDataRow[];
  columns: { key: string; label: string; fieldType?: string }[];
  total: number;
  groupCounts?: Record<string, number>;
};

export type CreateReportParams = {
  title: string;
  description: string;
  config: ReportConfig;
  status: 'published' | 'draft';
};

export type UpdateReportParams = {
  id: string;
  baseVersionId: string;
  title?: string;
  description?: string;
  config?: ReportConfig;
  status?: 'published' | 'draft'
};

export type GenerateReportDataParams = {
  config: ReportConfig;
  customQuery?: CustomQueryTable;
  page?: number;
  limit?: number;
  search?: string;
  sorting?: SortingState;
  viewFilters?: ReportFilterConfig;
  dashboardFilter?: any;
  debounceMs?: number;
};

export type GenerateReportDataImmediateParams = {
  config: ReportConfig;
  customQuery?: CustomQueryTable;
  page?: number;
  limit?: number;
  search?: string;
  sorting?: SortingState;
  viewFilters?: ReportFilterConfig;
  dashboardFilter?: any;
};

export type FetchReportUidsParams = {
  config: ReportConfig;
  customQuery?: CustomQueryTable;
  search?: string;
  viewFilters?: ReportFilterConfig;
  dashboardFilter?: any;
};

// Export types
export type ExportFormat = 'csv' | 'xlsx' | 'encrypted_csv';

export type ExportJobStatus = {
  taskId: string;
  sourceId: string;
  sourceType: string;
  format: ExportFormat;
  status: 'QUEUED' | 'STARTED' | 'FINISHED' | 'FAILED';
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
  result?: {
    success?: boolean;
    message?: string;
    data?: {
      downloadUrl?: string;
      filename?: string;
      path?: string;
    } | null;
    error?: {
      code?: string;
      message?: string;
      details?: unknown;
    };
  };
};

export type ExportMetadata = {
  exportJob?: ExportJobStatus;
  existingExports: {
    key: string;
    signedUrl?: string;
    filename: string;
    createdAt?: string;
  }[];
};

export type InitiateExportParams = {
  reportId: string;
  reportTitle?: string;
  format: ExportFormat;
  config?: ReportConfig;
  customQuery?: CustomQueryTable;
  viewFilters?: ReportFilterConfig;
  hiddenColumns?: string[];
};

type ReportsContextType = {
  reports: Array<Report>;
  allMergedReports: Array<Report>;
  getReports: (page?: number, limit?: number, search?: string, sorting?: SortingState) => Promise<void>;
  isReportsLoading: boolean;
  isReportsSearching: boolean;
  reportsTotal: number;
  reportsPage: number;
  reportsLimit: number;
  reportsTotalPages: number;
  reportsSearch: string;
  reportsSearchDisplay: string;
  reportsSorting: SortingState;

  // Pagination callbacks for reports list
  onReportsPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  onReportsSearchChange: (search: string) => void;
  onReportsSortingChange: React.Dispatch<React.SetStateAction<SortingState>>;

  transformDistroReportWithConfig: (report: any) => ReportWithConfig;

  deleteReportById: (id: string, fromDb?: boolean) => Promise<void>;
  cloneReport: (reportId: string, newTitle: string) => Promise<string | void>;
  isCloning: boolean;

  createReport: (params: CreateReportParams) => Promise<string | void>;
  updateReport: (params: UpdateReportParams) => Promise<void>;
  toggleReportLocked: (id: string) => Promise<boolean>;

  generateReportData: (params: GenerateReportDataParams) => Promise<ReportData | void>;
  generateReportDataImmediate: (params: GenerateReportDataImmediateParams) => Promise<ReportData | void>;
  /**
   * Fetches every uid matching the report (all pages) on demand, for the "Copy UIDs" control.
   * Order is deliberately undefined — the backend skips ORDER BY on the full uid set.
   */
  fetchReportUids: (params: FetchReportUidsParams) => Promise<string[]>;

  // Report builder state
  stagedConfig: ReportConfig | null;
  setStagedConfig: React.Dispatch<SetStateAction<ReportConfig | null>>;

  // Export functionality
  initiateReportExport: (params: InitiateExportParams) => Promise<{ taskId: string; status: string } | void>;
  getReportExportStatus: (reportId: string, taskId?: string) => Promise<ExportMetadata | void>;
  /** One-time reveal of an encrypted_csv export's AES key/IV, keyed by its S3 path. Returns null once already consumed. */
  getReportExportKey: (path: string) => Promise<{ keyMaterial: { aesKeyHex: string; ivHex: string } } | null | void>;
  /**
   * Downloads a "Download Decrypted" mode export: the backend decrypts its sensitive fields
   * in-stream and this fetches, then saves, the resulting plaintext CSV. Returns false on
   * failure (network error, or a same-status-200 JSON error body from the raw-response route).
   */
  downloadDecryptedReportExport: (params: { reportId: string; path: string; filename: string }) => Promise<boolean>;
  exportJobs: Record<string, ExportJobStatus>;
  isExporting: (reportId: string) => boolean;
};

type ReportsProviderProps = {
  children: React.ReactNode;
};

const ReportsContext = createContext<ReportsContextType>({
  reports: [],
  allMergedReports: [],
  getReports: async () => {},
  isReportsLoading: false,
  isReportsSearching: false,
  reportsTotal: 0,
  reportsPage: 1,
  reportsLimit: 25,
  reportsTotalPages: 0,
  reportsSearch: '',
  reportsSearchDisplay: '',
  reportsSorting: [],
  
  onReportsPaginationChange: () => {},
  onReportsSearchChange: () => {},
  onReportsSortingChange: () => {},

  transformDistroReportWithConfig: (_report: any) => ({
    id: '', title: '', description: '', created_by: '', created_by_name: '',
    created_at: 0, updated_at: 0, view_only: false, status: 'published' as const,
    config: { columns: [] },
  }),

  deleteReportById: async () => {},
  cloneReport: async (_reportId, _newTitle) => {},
  isCloning: false,

  createReport: async (_params) => {},
  updateReport: async (_params) => {},
  toggleReportLocked: async (_id) => false,

  generateReportData: async (_params) => {},
  generateReportDataImmediate: async (_params) => {},
  fetchReportUids: async (_params) => [],

  stagedConfig: null,
  setStagedConfig: (_prev) => {},

  // Export functionality defaults
  initiateReportExport: async (_params) => {},
  getReportExportStatus: async (_reportId, _taskId) => {},
  getReportExportKey: async (_path) => {},
  downloadDecryptedReportExport: async (_params) => false,
  exportJobs: {},
  isExporting: (_reportId) => false,
});

export const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error("useReports must be used within a ReportsProvider");
  }

  return context;
};

export const ReportsProvider: React.FC<ReportsProviderProps> = ({ children }) => {
  const interfaceContext = useContext(InterfaceContext);
  const queryClient = useQueryClient();
  const [reports, setReports] = useState<Array<Report>>([]);
  const [allMergedReports, setAllMergedReports] = useState<Array<Report>>([]);
  const [isReportsLoading, setIsReportsLoading] = useState(false);
  const [isReportsSearching, setIsReportsSearching] = useState(false);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsLimit, setReportsLimit] = useState(10);
  const [reportsTotalPages, setReportsTotalPages] = useState(0);
  const [reportsSearch, setReportsSearch] = useState(''); // For API calls
  const [reportsSearchDisplay, setReportsSearchDisplay] = useState(''); // For UI display
  const [reportsSorting, setReportsSorting] = useState<SortingState>([]);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generateDataTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [stagedConfig, setStagedConfig] = useState<ReportConfig | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (generateDataTimeoutRef.current) {
        clearTimeout(generateDataTimeoutRef.current);
      }
    };
  }, []);

  const listReportsPost = usePost("/distro/report/list");
  const getReportPost = usePost("/distro/report/get");
  const getAdminPanelReports = usePost("/api/admin_panel/reports");
  const fetchReportByIdPost = usePost("/api/admin_panel/report");
  const createReportPost = usePost("/distro/report/create");
  const updateReportPost = usePost("/distro/report/update");
  const deleteReportPost = usePost("/distro/report/delete");
  const deleteAdminPanelReportPost = usePost("/api/admin_panel/reports/delete");
  const generateReportDataPost = usePost("/api/admin_panel/reports/generate");
  const exportReportAsyncPost = usePost("/api/admin_panel/reports/export");
  const getReportExportStatusPost = usePost("/api/admin_panel/reports/export/status");
  const getReportExportKeyPost = usePost("/api/admin_panel/reports/export/key");
  const downloadDecryptedReportExportPost = usePost("/api/admin_panel/reports/export/download_decrypted", { asBlob: true });

  // Localized Report[] — must include lang (matches distroReportsQueryKey in commandPaletteReports).
  const DISTRO_REPORTS_KEY = ['distro-reports', interfaceContext.lang] as const;

  const invalidateDistroCache = useCallback(() => {
    // Prefix match clears every language variant.
    queryClient.removeQueries({ queryKey: ['distro-reports'] });
  }, [queryClient]);

  const transformDistroReport = useCallback((report: any): Report => ({
    id: report.id,
    title: report.name?.[interfaceContext.lang] || report.name?.en || '',
    description: report.description?.[interfaceContext.lang] || report.description?.en || '',
    created_by: report.author || 'system',
    created_by_name: report.author_name || 'System',
    created_at: 0,
    updated_at: report.updated_at || 0,
    view_only: false,
    status: 'published',
    config: report.table ? {
      columns: report.table.columns || [],
      filter: report.table.filter,
      ...(report.groupBy && { groupBy: report.groupBy }),
      ...(report.table?.linksToSection && { linksToSection: report.table.linksToSection }),
      ...(report?.encryption && { encryption: report.encryption }),
    } : undefined,
    _isCustomQuery: report.table?.kind === 'Custom Query',
    locked: report.locked,
  }), [interfaceContext.lang]);

  const transformDistroReportWithConfig = useCallback((report: any): ReportWithConfig => ({
    id: report.id,
    title: report.name?.[interfaceContext.lang] || report.name?.en || '',
    description: report.description?.[interfaceContext.lang] || report.description?.en || '',
    created_by: report.author || 'system',
    created_by_name: report.author_name || 'System',
    created_at: 0,
    updated_at: report.updated_at || 0,
    view_only: false,
    status: 'published',
    config: {
      columns: report.table?.columns || [],
      filter: report.table?.filter,
      ...(report.groupBy && { groupBy: report.groupBy }),
      ...(report.table?.linksToSection && { linksToSection: report.table.linksToSection }),
      ...(report.table?.usePivot && { usePivot: report.table.usePivot }),
      ...(report.encryption && { encryption: report.encryption }),
    },
    _isCustomQuery: report.table?.kind === 'Custom Query',
    locked: report.locked,
  }), [interfaceContext.lang]);

  // Export state
  const [exportJobs, setExportJobs] = useState<Record<string, ExportJobStatus>>({});
  const exportPollingRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const getReports = async (page = 1, limit = 10, search?: string, sorting?: SortingState) => {
    console.log('[ReportsContext] getReports called with:', { page, limit, search, sorting });
    const searchParam = search !== undefined ? search : reportsSearch;
    const sortingParam = sorting !== undefined ? sorting : reportsSorting;

    // Set appropriate loading state
    const isSearchOperation = searchParam !== reportsSearch;
    if (isSearchOperation) {
      setIsReportsSearching(true);
    } else {
      setIsReportsLoading(true);
    }

    try {
      // Check cache first. Empty arrays are truthy — only write the cache when both
      // fetches succeed, otherwise a transient failure freezes the list as empty.
      let mergedReports = queryClient.getQueryData<Report[]>(DISTRO_REPORTS_KEY);
      console.log('[ReportsContext] Cache status:', mergedReports ? `cached (${mergedReports.length} items)` : 'empty, will fetch');
      if (!mergedReports) {
        // Fetch from both distro and DB in parallel
        console.log('[ReportsContext] Fetching from both distro and DB...');
        const [distroResult, dbResult] = await Promise.all([
          listReportsPost({}).catch((err: unknown) => {
            console.warn('[ReportsContext] Distro fetch failed:', err);
            return null;
          }),
          getAdminPanelReports({}).catch((err: unknown) => {
            console.warn('[ReportsContext] DB fetch failed:', err);
            return null;
          }),
        ]);

        const distroOk = Array.isArray(distroResult);
        const dbOk = dbResult != null && typeof dbResult === 'object' && 'data' in dbResult;

        // Transform distro reports
        const distroReports = distroOk ? distroResult : [];
        const transformedDistro = distroReports.map(transformDistroReport);

        // Transform DB reports
        const dbReports: Report[] = [];
        const dbData =
          dbOk && dbResult && typeof dbResult === 'object' && 'data' in dbResult
            ? (dbResult as {
              data?: {
                reports?: {
                  data?: Array<{
                    id: string;
                    title: string;
                    description?: string;
                    created_by: string;
                    created_by_name: string;
                    created_at: number;
                    updated_at: number;
                    view_only: boolean;
                    status: 'draft' | 'published';
                  }>;
                };
              };
            }).data
            : null;
        if (dbData?.reports?.data) {
          for (const r of dbData.reports.data) {
            dbReports.push({
              id: r.id,
              title: r.title,
              description: r.description || '',
              created_by: r.created_by,
              created_by_name: r.created_by_name,
              created_at: r.created_at,
              updated_at: r.updated_at,
              view_only: r.view_only,
              status: r.status,
              _fromDb: true,
            });
          }
        }

        // Merge: DB first, then distro overwrites on ID collision
        const reportsMap = new Map<string, Report>();
        for (const r of dbReports) {
          reportsMap.set(r.id, r);
        }
        for (const r of transformedDistro) {
          reportsMap.set(r.id, r);
        }
        mergedReports = Array.from(reportsMap.values());
        console.log('[ReportsContext] Merged reports count:', mergedReports.length, `(${transformedDistro.length} distro, ${dbReports.length} DB)`);
        if (distroOk && dbOk) {
          queryClient.setQueryData(DISTRO_REPORTS_KEY, mergedReports);
        }
      }

      setAllMergedReports(mergedReports);

      // Apply client-side search, sorting, and pagination
      let transformed = mergedReports;

      // Client-side search
      if (searchParam && searchParam.trim()) {
        const searchLower = searchParam.trim().toLowerCase();
        const dateFormatter = new Intl.DateTimeFormat("en-US", {
          month: "short", day: "numeric", year: "numeric",
        });
        transformed = transformed.filter(r =>
          r.title.toLowerCase().includes(searchLower) ||
          r.description.toLowerCase().includes(searchLower) ||
          r.created_by_name.toLowerCase().includes(searchLower) ||
          (r.updated_at && dateFormatter.format(new Date(r.updated_at)).toLowerCase().includes(searchLower))
        );
      }

      // Client-side sorting
      if (sortingParam && sortingParam.length > 0) {
        transformed.sort((a, b) => {
          for (const sort of sortingParam) {
            const aVal = (a as any)[sort.id] ?? '';
            const bVal = (b as any)[sort.id] ?? '';
            const cmp = String(aVal).localeCompare(String(bVal));
            if (cmp !== 0) return sort.desc ? -cmp : cmp;
          }
          return 0;
        });
      } else {
        // Default sort: most recently updated first
        transformed.sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
      }

      // Client-side pagination
      const total = transformed.length;
      const totalPages = Math.ceil(total / limit);
      const startIdx = (page - 1) * limit;
      const paginatedReports = transformed.slice(startIdx, startIdx + limit);

      setReports(paginatedReports);
      setReportsTotal(total);
      setReportsPage(page);
      setReportsLimit(limit);
      setReportsTotalPages(totalPages);

      if (search !== undefined) {
        setReportsSearch(search);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setIsReportsLoading(false);
      setIsReportsSearching(false);
    }
  };

  // Use ref to avoid circular dependencies
  const getReportsRef = useRef(getReports);
  getReportsRef.current = getReports;

  const onReportsPaginationChange = useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    // Convert 0-based pageIndex to 1-based page for API
    const page = pagination.pageIndex + 1;
    const limit = pagination.pageSize;
    
    // If page size changed, always go to page 1
    if (limit !== reportsLimit) {
      getReportsRef.current(1, limit, reportsSearch, reportsSorting);
    } else {
      getReportsRef.current(page, limit, reportsSearch, reportsSorting);
    }
  }, [reportsLimit, reportsSearch, reportsSorting]);

  const onReportsSearchChange = useCallback((search: string) => {
    // Clear existing timeout first
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Update display state immediately for UI feedback
    setReportsSearchDisplay(search);
    
    const trimmedSearch = search.trim();
    
    // If search is empty, call API immediately (clearing search)
    if (!trimmedSearch) {
      setIsReportsSearching(false);
      setReportsSearch(search);
      getReportsRef.current(1, reportsLimit, search, reportsSorting);
      return;
    }
    
    // For non-empty search, show searching state and debounce the API call
    setIsReportsSearching(true);
    
    const timeoutId = setTimeout(() => {
      setReportsSearch(search); // Update API state after debounce
      getReportsRef.current(1, reportsLimit, search, reportsSorting);
    }, 400);
    
    searchTimeoutRef.current = timeoutId;
  }, [reportsLimit, reportsSorting]);

  const onReportsSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(reportsSorting) : updaterOrValue;
    setReportsSorting(newSorting);
    // Fetch data with new sorting, reset to first page
    getReportsRef.current(1, reportsLimit, reportsSearch, newSorting);
  }, [reportsSorting, reportsLimit, reportsSearch]);

  const deleteReportById = async (id: string, fromDb?: boolean) => {
    try {
      if (fromDb) {
        await deleteAdminPanelReportPost({ reportId: id });
      } else {
        await deleteReportPost({ id });
      }

      // Remove from query cache
      queryClient.removeQueries({ queryKey: ['report', id] });

      // Remove from every language-scoped distro reports cache
      queryClient.setQueriesData<Report[]>(
        { queryKey: ['distro-reports'] },
        (cachedReports) => (cachedReports ? cachedReports.filter(r => r.id !== id) : cachedReports)
      );

      // Remove from the current reports list
      setReports(prev => prev.filter(r => r.id !== id));
      setAllMergedReports(prev => prev.filter(r => r.id !== id));
      setReportsTotal(prev => prev - 1);
    } catch (error) {
      console.error('Error deleting report:', error);
      throw new Error('Failed to delete report');
    }
  };

  // Clone report: get existing report from distro, then create a copy with new title
  const cloneReportMutation = useMutation({
    mutationFn: async ({ reportId, newTitle }: { reportId: string; newTitle: string }) => {
      const existing = await getReportPost({ id: reportId });

      const clonedConfig = {
        kind: 'Report' as const,
        name: { ...existing.result.name, en: newTitle },
        description: existing.result.description,
        table: existing.result.table,
      };

      const createResponse = await createReportPost({ config: clonedConfig });
      const created = createResponse?.result;
      if (created?.id) {
        queryClient.removeQueries({ queryKey: ['report', created.id] });
        return created.id as string;
      }
      throw new Error('Failed to clone report');
    },
    onSuccess: async () => {
      invalidateDistroCache();
      await getReports(reportsPage, reportsLimit, reportsSearch, reportsSorting);
    },
  });

  const cloneReport = useCallback(async (reportId: string, newTitle: string): Promise<string | void> => {
    try {
      return await cloneReportMutation.mutateAsync({ reportId, newTitle });
    } catch (error) {
      console.error('Error cloning report:', error);
      return undefined;
    }
  }, [cloneReportMutation]);

  const isCloning = cloneReportMutation.isPending;

  const createReport = async ({ title, description, config }: CreateReportParams) => {
    try {
      const result = await createReportPost({
        config: {
          kind: 'Report' as const,
          name: { en: title },
          ...(description ? { description: { kind: 'RichText' as const, en: description } } : {}),
          table: {
            kind: 'Applicant Table' as const,
            columns: config.columns,
            filter: config.filter ?? { kind: 'Not', clause: { kind: 'Never' } },
            ...(config.linksToSection && { linksToSection: config.linksToSection }),
          },
          ...(config.groupBy && { groupBy: config.groupBy }),
          ...(config?.encryption?.encryptedFields?.length && { encryption: config?.encryption }),
        },
      });

      const created = result?.result;
      if (created && typeof created === 'object' && 'id' in created &&
          typeof created.id === 'string') {
        invalidateDistroCache();
        queryClient.removeQueries({ queryKey: ['report', created.id] });

        return created.id;
      }
    } catch (error) {
      console.error('Error creating report:', error);
    }
  };

  const updateReport = async ({ id, baseVersionId, title, description, config }: UpdateReportParams) => {
    try {
      // Fetch existing report to merge changes
      const existing = await getReportPost({ id });

      const encryptedFields = config?.encryption?.encryptedFields
        ?? existing.result?.encryption?.encryptedFields;
      const encryptionMode = config?.encryption?.mode
        ?? existing.result?.encryption?.mode

      const updatedConfig = {
        kind: 'Report' as const,
        name: title !== undefined ? { ...existing.result.name, en: title } : existing.result.name,
        description: description !== undefined ? { kind: 'RichText' as const, ...existing.result.description, en: description } : existing.result.description,
        table: config !== undefined ? {
          kind: 'Applicant Table' as const,
          columns: config.columns,
          filter: config.filter ?? { kind: 'Not' as const, clause: { kind: 'Never' as const } },
          ...(config.linksToSection && { linksToSection: config.linksToSection }),
        } : existing.result.table,
        ...(config?.groupBy ? { groupBy: config.groupBy } : { groupBy: undefined }),
        ...((encryptedFields && encryptionMode) && { encryption: { encryptedFields, mode: encryptionMode } }),
        // Preserve fields managed outside the edit form
        ...(existing.result.locked !== undefined && { locked: existing.result.locked }),
        ...(existing.result.author && { author: existing.result.author }),
      };

      const updateResponse = await updateReportPost({ id, config: updatedConfig, baseVersionId });

      if ('error' in updateResponse) {
        throw new Error(String(updateResponse.error));
      }

      invalidateDistroCache();
      queryClient.removeQueries({ queryKey: ['report', id] });
    } catch (error) {
      console.error('Error updating report:', error);
      throw new Error(`Failed to update report ${id}`);
    }
  };

  const toggleReportLocked = useCallback(async (id: string): Promise<boolean> => {
    try {
      // Fetch the latest report from distro to get current locked state and versionId
      const existing = await getReportPost({ id });
      const baseVersionId = existing.versionId;
      const newLocked = !existing.result.locked;

      await updateReportPost({
        id,
        config: { partialUpdate: true, fields: { locked: newLocked } },
        baseVersionId,
      });

      // Update local state immediately
      setReports(prev => prev.map(r => r.id === id ? { ...r, locked: newLocked } : r));
      setAllMergedReports(prev => prev.map(r => r.id === id ? { ...r, locked: newLocked } : r));

      // Invalidate both list and individual report caches
      invalidateDistroCache();
      queryClient.removeQueries({ queryKey: ['report', id] });

      return newLocked;
    } catch (error) {
      console.error('Error toggling report lock:', error);
      throw error;
    }
  }, [getReportPost, updateReportPost, invalidateDistroCache]);

  // Immediate version of generateReportData (no debouncing)
  const generateReportDataImmediate = useCallback(async ({ config, customQuery, page = 1, limit = 50, search, sorting, viewFilters, dashboardFilter }: GenerateReportDataImmediateParams): Promise<ReportData | void> => {
    const params: any = { config, page, limit };
    if (customQuery) {
      params.customQuery = customQuery;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }

    // Add viewFilters if provided
    if (viewFilters) {
      params.viewFilters = viewFilters;
    }

    // Add dashboardFilter if provided
    if (dashboardFilter) {
      params.dashboardFilter = dashboardFilter;
    }

    // Add sorting parameters if provided
    if (sorting && sorting.length > 0) {
      params.sorting = sorting.map(sort => ({
        sortBy: sort.id,
        sortOrder: sort.desc ? 'desc' as const : 'asc' as const
      }));
    }
    
    try {
      const result = await generateReportDataPost(params);

      if (result.success && result.data) {
        return {
          data: result.data.data,
          columns: result.data.columns,
          total: result.data.total,
          ...(result.data.groupCounts && { groupCounts: result.data.groupCounts })
        };
      } else if (result.error) {
        logAdminPanelError(result.error, result.message);
      }
    } catch (error) {
      throw error;
    }
  }, [generateReportDataPost]);

  // Fetches every uid matching the report via the generate endpoint in uidsOnly mode — no
  // pagination, no page data. Called on demand by the "Copy UIDs" control (never debounced).
  const fetchReportUids = useCallback(async ({ config, customQuery, search, viewFilters, dashboardFilter }: FetchReportUidsParams): Promise<string[]> => {
    const params: any = { config, uidsOnly: true };
    if (customQuery) {
      params.customQuery = customQuery;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (viewFilters) {
      params.viewFilters = viewFilters;
    }
    if (dashboardFilter) {
      params.dashboardFilter = dashboardFilter;
    }

    const result = await generateReportDataPost(params);

    if (result.success && result.data) {
      // uidsOnly responses are a normal report result whose rows carry only uid.
      const rows = (result.data.data as Array<{ uid: string }> | undefined) ?? [];
      return rows.map(row => row.uid);
    }
    if (result.error) {
      logAdminPanelError(result.error, result.message);
    }
    throw new Error(result.message || 'Failed to fetch report uids');
  }, [generateReportDataPost]);

  // Debounced version of generateReportData
  const generateReportData = useCallback(async ({ config, customQuery, page = 1, limit = 50, search, sorting, viewFilters, dashboardFilter, debounceMs = 800 }: GenerateReportDataParams): Promise<ReportData | void> => {
    return new Promise((resolve, reject) => {
      // Clear any existing timeout
      if (generateDataTimeoutRef.current) {
        clearTimeout(generateDataTimeoutRef.current);
      }

      // Set up new timeout
      generateDataTimeoutRef.current = setTimeout(async () => {
        try {
          const result = await generateReportDataImmediate({ config, customQuery, page, limit, search, sorting, viewFilters, dashboardFilter });
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, debounceMs);
    });
  }, [generateReportDataImmediate]);

  // Export functionality
  const initiateReportExport = useCallback(async ({ reportId, reportTitle, format, config, customQuery, viewFilters, hiddenColumns }: InitiateExportParams): Promise<{ taskId: string; status: string } | void> => {
    try {
      const result = await exportReportAsyncPost({ reportId, reportTitle, format, config, customQuery, viewFilters, hiddenColumns });

      if (result.success && result.data) {
        const { taskId, status } = result.data;

        // Set initial job status
        setExportJobs(prev => ({
          ...prev,
          [reportId]: {
            taskId,
            sourceId: reportId,
            sourceType: 'report',
            format,
            status: status as ExportJobStatus['status'],
            createdAt: new Date().toISOString(),
          }
        }));

        // Start polling for status
        startExportPolling(reportId, taskId);

        return { taskId, status };
      } else if (result.error) {
        logAdminPanelError(result.error, result.message);
      }
    } catch (error) {
      console.error('Error initiating report export:', error);
    }
  }, [exportReportAsyncPost]);

  const getReportExportStatus = useCallback(async (reportId: string, taskId?: string): Promise<ExportMetadata | void> => {
    try {
      const result = await getReportExportStatusPost({ reportId, taskId });

      if (result.success && result.data) {
        return result.data as ExportMetadata;
      } else if (result.error) {
        logAdminPanelError(result.error, result.message);
      }
    } catch (error) {
      console.error('Error getting report export status:', error);
    }
  }, [getReportExportStatusPost]);

  // One-time reveal: the backend deletes the key/IV on read, so this only ever
  // returns a real value once per export. Caller must hold it in memory only -
  // never persist it (localStorage, etc).
  const getReportExportKey = useCallback(async (path: string): Promise<{ keyMaterial: { aesKeyHex: string; ivHex: string } } | null | void> => {
    try {
      const result = await getReportExportKeyPost({ path });

      if (result.success) {
        return result.data as { keyMaterial: { aesKeyHex: string; ivHex: string } } | null;
      } else if (result.error) {
        logAdminPanelError(result.error, result.message);
      }
    } catch (error) {
      console.error('Error getting report export key:', error);
    }
  }, [getReportExportKeyPost]);

  // Fetches the plaintext CSV (decrypted server-side, in-stream) and saves it - unlike
  // getReportExportKey, there's no secret for the caller to hold, just the finished file.
  //
  // Fetched as a Blob rather than text: reading the (potentially large) body as text would
  // decode the whole thing into a JS string, then `new Blob([text])` would re-encode it again
  // before the browser could save anything - three copies of the file alive in memory at once.
  // `asBlob` keeps it to one.
  const downloadDecryptedReportExport = useCallback(async (params: {
    reportId: string; path: string; filename: string;
  }): Promise<boolean> => {
    try {
      // The route is a raw-response route (returns `Promise<void>` server-side, hence the
      // `unknown` cast) - `asBlob` makes the actual runtime value a Blob.
      const result = await downloadDecryptedReportExportPost(params) as unknown;
      if (!(result instanceof Blob)) {
        return false;
      }

      // Raw-response routes on this backend signal errors via a JSON body kept at HTTP 200
      // (see the backend's sendJsonError). Its content-type ('application/json' vs the real
      // export's 'text/csv') tells the two apart without reading the CSV body as text first -
      // the error body is small, so buffering just that one as text is fine.
      if (result.type === 'application/json') {
        const maybeError = JSON.parse(await result.text());
        console.error('Error downloading decrypted report export:', maybeError?.error ?? maybeError);
        return false;
      }

      const link = document.createElement('a');
      link.href = URL.createObjectURL(result);
      link.download = params.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      return true;
    } catch (error) {
      console.error('Error downloading decrypted report export:', error);
      return false;
    }
  }, [downloadDecryptedReportExportPost]);

  const startExportPolling = useCallback((reportId: string, taskId: string) => {
    // Clear any existing polling for this report
    if (exportPollingRef.current[reportId]) {
      clearInterval(exportPollingRef.current[reportId]);
    }

    // Poll every 3 seconds
    const intervalId = setInterval(async () => {
      const metadata = await getReportExportStatus(reportId, taskId);

      if (metadata?.exportJob) {
        const job = metadata.exportJob;

        setExportJobs(prev => ({
          ...prev,
          [reportId]: job
        }));

        // Stop polling when job is complete or failed
        if (job.status === 'FINISHED' || job.status === 'FAILED') {
          clearInterval(exportPollingRef.current[reportId]);
          delete exportPollingRef.current[reportId];
        }
      }
    }, 3000);

    exportPollingRef.current[reportId] = intervalId;
  }, [getReportExportStatus]);

  const isExporting = useCallback((reportId: string): boolean => {
    const job = exportJobs[reportId];
    return job?.status === 'QUEUED' || job?.status === 'STARTED';
  }, [exportJobs]);

  // Cleanup export polling on unmount
  useEffect(() => {
    return () => {
      Object.values(exportPollingRef.current).forEach(intervalId => {
        clearInterval(intervalId);
      });
    };
  }, []);

  const value = {
    reports,
    allMergedReports,
    getReports,
    isReportsLoading,
    isReportsSearching,
    reportsTotal,
    reportsPage,
    reportsLimit,
    reportsTotalPages,
    reportsSearch,
    reportsSearchDisplay,
    reportsSorting,
    
    onReportsPaginationChange,
    onReportsSearchChange,
    onReportsSortingChange,

    transformDistroReportWithConfig,
    deleteReportById,
    cloneReport,
    isCloning,

    createReport,
    updateReport,
    toggleReportLocked,

    generateReportData,
    generateReportDataImmediate,
    fetchReportUids,

    stagedConfig,
    setStagedConfig,

    // Export functionality
    initiateReportExport,
    getReportExportStatus,
    getReportExportKey,
    downloadDecryptedReportExport,
    exportJobs,
    isExporting,
  };

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  );
};
